import { ref, computed } from 'vue'

/**
 * Composable for handling image loading errors and providing fallbacks
 */
export function useImageErrorHandler() {
  const erroredImages = ref(new Set<string>())
  const loadingImages = ref(new Set<string>())

  /**
   * Check if an image URL is safe to use (not null, undefined, or previously errored)
   * @param imageUrl - The image URL to check
   * @returns boolean indicating if the URL is safe
   */
  const isImageSafe = (imageUrl: string | null | undefined): boolean => {
    if (!imageUrl || typeof imageUrl !== 'string') return false
    if (imageUrl.trim() === '') return false
    return !erroredImages.value.has(imageUrl)
  }

  /**
   * Get a safe image URL with fallback
   * @param imageUrl - Primary image URL
   * @param fallbackUrl - Optional fallback URL
   * @returns A safe image URL or null if none available
   */
  const getSafeImageUrl = (
    imageUrl: string | null | undefined, 
    fallbackUrl?: string
  ): string | null => {
    if (isImageSafe(imageUrl)) return imageUrl!
    if (fallbackUrl && isImageSafe(fallbackUrl)) return fallbackUrl
    return null
  }

  /**
   * Handle image error by adding URL to error set
   * @param imageUrl - The URL that failed to load
   */
  const handleImageError = (imageUrl: string) => {
    erroredImages.value.add(imageUrl)
    loadingImages.value.delete(imageUrl)
    console.warn(`Image failed to load: ${imageUrl}`)
  }

  /**
   * Handle image load success by removing from loading set
   * @param imageUrl - The URL that loaded successfully
   */
  const handleImageLoad = (imageUrl: string) => {
    loadingImages.value.delete(imageUrl)
  }

  /**
   * Start loading an image
   * @param imageUrl - The URL being loaded
   */
  const startImageLoad = (imageUrl: string) => {
    loadingImages.value.add(imageUrl)
  }

  /**
   * Check if an image is currently loading
   * @param imageUrl - The URL to check
   */
  const isImageLoading = (imageUrl: string): boolean => {
    return loadingImages.value.has(imageUrl)
  }

  /**
   * Create image event handlers for a specific URL
   * @param imageUrl - The image URL
   * @returns Object with error and load handlers
   */
  const createImageHandlers = (imageUrl: string) => ({
    onError: () => handleImageError(imageUrl),
    onLoad: () => handleImageLoad(imageUrl)
  })

  /**
   * Validate image data structure from GraphQL
   * @param imageData - Image data from GraphQL response
   * @returns boolean indicating if data is valid
   */
  const isValidImageData = (imageData: any): boolean => {
    return !!(
      imageData?.data?.attributes?.url &&
      typeof imageData.data.attributes.url === 'string' &&
      imageData.data.attributes.url.trim() !== ''
    )
  }

  /**
   * Get image URL from GraphQL data structure
   * @param imageData - Image data from GraphQL
   * @returns Image URL or null if invalid
   */
  const getImageUrl = (imageData: any): string | null => {
    if (!isValidImageData(imageData)) return null
    return imageData.data.attributes.url
  }

  /**
   * Get image formats from GraphQL data structure
   * @param imageData - Image data from GraphQL
   * @returns Image formats object or null if not available
   */
  const getImageFormats = (imageData: any): any => {
    if (!isValidImageData(imageData)) return null
    return imageData.data.attributes.formats || null
  }

  /**
   * Check if image data has responsive formats
   * @param imageData - Image data from GraphQL
   * @returns boolean indicating if formats are available
   */
  const hasImageFormats = (imageData: any): boolean => {
    return !!(imageData?.data?.attributes?.formats)
  }

  /**
   * Get image alt text from GraphQL data structure
   * @param imageData - Image data from GraphQL
   * @param fallbackAlt - Fallback alt text
   * @returns Alt text or fallback
   */
  const getImageAlt = (imageData: any, fallbackAlt: string = ''): string => {
    if (!isValidImageData(imageData)) return fallbackAlt
    return imageData.data.attributes.alternativeText || fallbackAlt
  }

  /**
   * Clear all error state (useful for testing or manual reset)
   */
  const clearErrors = () => {
    erroredImages.value.clear()
    loadingImages.value.clear()
  }

  /**
   * Get the current error count (useful for debugging)
   */
  const errorCount = computed(() => erroredImages.value.size)

  return {
    isImageSafe,
    getSafeImageUrl,
    handleImageError,
    handleImageLoad,
    startImageLoad,
    isImageLoading,
    createImageHandlers,
    isValidImageData,
    getImageUrl,
    getImageAlt,
    getImageFormats,
    hasImageFormats,
    clearErrors,
    errorCount
  }
}

/**
 * Default placeholder images for different content types
 */
export const PLACEHOLDER_IMAGES = {
  CASE_STUDY: '/src/assets/img/placeholders/case-study-placeholder.svg',
  TEAM_MEMBER: '/src/assets/img/placeholders/team-member-placeholder.svg',
  CLIENT_LOGO: '/src/assets/img/placeholders/client-logo-placeholder.svg',
  GENERAL: '/src/assets/img/placeholders/general-placeholder.svg'
} as const
